import Layout from '@/components/feature/Layout';
import { PageBreadcrumb } from '@/pages/shared/PageHelpers';
import { applications } from '@/mocks/applications';
import { companyInfo } from '@/mocks/company';
import { Link } from 'react-router-dom';

const whatsappMessage = encodeURIComponent('Olá! Estou no site da Maquinasdesoldageomembrana.com.br e gostaria de informações sobre máquinas de solda de geomembrana.');
const whatsappUrl = `https://wa.me/${companyInfo.whatsapp}?text=${whatsappMessage}`;

const schema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Aplicações das Máquinas de Solda de Geomembrana',
  description:
    'Conheça todas as aplicações das máquinas de solda de geomembrana: aterros sanitários, mineração, lagoas, piscicultura, saneamento, agricultura e soldagem de lona de PVC.',
  url: 'https://maquinadesoldageomembrana.com.br/aplicacoes',
  inLanguage: 'pt-BR',
};

export default function AplicacoesPage() {
  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <PageBreadcrumb items={[{ label: 'Aplicações' }]} />

        <div className="max-w-4xl mb-12 md:mb-16">
          <span className="text-primary-500 text-xs uppercase tracking-widest font-bold mb-3 block">Onde Utilizar</span>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 uppercase leading-tight mb-5">
            Aplicações das Máquinas de Solda de Geomembrana
          </h1>
          <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
            As máquinas de solda de geomembrana atendem projetos de impermeabilização nos mais diversos setores no Brasil.
            Cada aplicação exige um tipo de material e de equipamento. Abaixo você entende, setor por setor, para que serve
            cada aplicação e quais modelos são os mais indicados para cada obra.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-14 md:mb-20">
          {applications.map((app) => (
            <Link
              key={app.id}
              to={`/aplicacoes/${app.id}`}
              className="group bg-white rounded-2xl border border-background-200/70 overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 cursor-pointer"
            >
              <div className="relative w-full h-44 md:h-48 overflow-hidden">
                <img
                  alt={`${app.title} — aplicação de máquina de solda geomembrana`}
                  title={`${app.title} — máquina de solda geomembrana`}
                  className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  src={app.image}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent" />
                <div className="absolute top-3 left-3 w-9 h-9 flex items-center justify-center rounded-lg bg-white/90">
                  <i className={`${app.icon} text-primary-600 text-lg`} />
                </div>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 uppercase mb-1">{app.title}</h2>
                <p className="text-foreground-400 text-xs font-medium mb-3">{app.tagline}</p>
                <p className="text-foreground-500 text-sm leading-relaxed mb-5 flex-1">{app.description}</p>
                <span className="inline-flex items-center gap-2 text-sm font-bold text-primary-600 group-hover:text-primary-700 uppercase tracking-wide">
                  Ver detalhes
                  <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="bg-background-100 rounded-2xl border border-background-200/70 p-6 md:p-10 mb-14 md:mb-20">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 uppercase mb-4">
            Como escolher a aplicação certa
          </h2>
          <p className="text-foreground-500 text-sm md:text-base leading-relaxed max-w-3xl mb-8">
            A escolha do equipamento depende do material a ser soldado e do tipo de projeto. Enquanto as obras de
            impermeabilização de aterros, mineração, lagoas e reservatórios utilizam geomembrana de PEAD, PVC ou LLDPE,
            a soldagem de lonas de PVC (banners, toldos e coberturas) exige um equipamento específico e diferente.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-background-200/70 p-5">
              <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-full mb-3">
                <i className="ri-shield-check-line text-primary-600 text-lg" />
              </div>
              <h3 className="text-sm font-bold text-foreground-900 uppercase mb-1">Geomembrana</h3>
              <p className="text-foreground-500 text-xs leading-relaxed">
                Impermeabilização de aterros, barragens, lagoas, mineração, piscicultura e saneamento. Utiliza cunha
                quente automática, cunha quente manual e extrusora para PEAD, PVC e LLDPE.
              </p>
            </div>
            <div className="bg-white rounded-xl border border-background-200/70 p-5">
              <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-full mb-3">
                <i className="ri-umbrella-line text-accent-600 text-lg" />
              </div>
              <h3 className="text-sm font-bold text-foreground-900 uppercase mb-1">Lona de PVC</h3>
              <p className="text-foreground-500 text-xs leading-relaxed">
                Soldagem de banners, toldos, coberturas e estruturas tensionadas. Utiliza a máquina de ar quente de alta
                pressão LC3000C, que é exclusiva para lona de PVC e não é indicada para geomembrana.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-primary-50 rounded-2xl border border-primary-200/70 p-6 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mb-2">
              Não sabe qual equipamento usar na sua obra?
            </h2>
            <p className="text-foreground-500 text-sm">
              Fale com nossa equipe técnica e receba a indicação ideal para o seu projeto em minutos.
            </p>
          </div>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-sm px-8 py-4 rounded-full transition-all cursor-pointer whitespace-nowrap uppercase tracking-wide flex-shrink-0"
          >
            <i className="ri-whatsapp-line" /> Falar no WhatsApp
          </a>
        </div>
      </div>
    </Layout>
  );
}